import React, { forwardRef } from "react";
import styled from "styled-components";
import { tokens } from "../../public/tokens";
import { StyledHiddenRadio } from "./Radio";

const { colors, typography, globals, effects } = tokens;

const StyledCard = styled.label(({ $disabled = false }) => ({
    position: "relative",
    margin: 0,
    display: "flex",
    alignItems: "flex-start",
    gap: globals["spacing-2"],
    padding: globals["spacing-4"],
    borderRadius: globals["border-radius"],
    border: `${globals["spacing-px"]} solid ${colors.ui.border}`,
    background: colors.ui.card,
    boxShadow: effects.effects.shadow.default,
    cursor: $disabled ? "not-allowed" : "pointer",
    opacity: $disabled ? 0.5 : 1,
    transition: "all 0.15s ease-in-out",

    "input:focus-visible + span": {
        outline: `2px solid ${colors.brand.blue["500"]}`,
        outlineOffset: globals["spacing-0-5"],
    },
}));

/*
 * The checked dot is the blue fill showing through a 3px inset ring, so it stays round and
 * centred regardless of legacy pseudo-element CSS. The 1px top margin centres the 16px
 * circle on the title's 110% line height.
 */
const StyledIndicator = styled.span(({ $checked }) => ({
    boxSizing: "border-box",
    display: "block",
    flexShrink: 0,
    width: globals["spacing-4"],
    height: globals["spacing-4"],
    marginTop: globals["spacing-px"],
    borderRadius: "50%",
    border: `${globals["spacing-px"]} solid ${$checked ? colors.brand.blue["500"] : colors.ui.border}`,
    background: $checked ? colors.brand.blue["500"] : colors.ui.card,
    boxShadow: $checked ? `inset 0 0 0 3px ${colors.brand.blue["50"]}` : "none",
    transition: "all 0.15s ease-in-out",

    "label:hover > &": {
        borderColor: colors.brand.blue["500"],
    },
}));

const StyledText = styled.span({
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: globals["spacing-0-5"],
    fontFamily: typography.fontFamilies["albert-sans"],
    lineHeight: typography.lineHeights["110"],
});

const StyledTitle = styled.span({
    fontSize: typography.fontSize["base-16px"],
    fontWeight: typography.fontWeights.semibold,
    color: colors.text.primary,
});

const StyledDescription = styled.span({
    fontSize: typography.fontSize["lg-14px"],
    fontWeight: typography.fontWeights.regular,
    color: colors.text.secondary,
});

const StyledIcon = styled.i(({ $color }) => ({
    width: globals["spacing-6"],
    flexShrink: 0,
    textAlign: "center",
    fontSize: typography.fontSize["xl-18px"],
    color: $color || colors.text.primary,
}));

/**
 * Bordered, selectable tile with a radio, a title, a description and an icon on the right.
 * @param {React.ReactNode} props.title Card heading.
 * @param {React.ReactNode} [props.description] Muted line under the title.
 * @param {string} [props.icon] Font Awesome class, e.g. "fal fa-file".
 * @param {string} [props.iconColor] Icon colour token; defaults to primary text.
 * Also: name, value, checked, disabled, onChange, className (forwarded to the radio input / card).
 */
const RadioCard = forwardRef(function RadioCard(
    { title, description, icon, iconColor, checked = false, disabled = false, className, ...inputProps },
    ref
) {
    return (
        <StyledCard className={className} $disabled={disabled}>
            <StyledHiddenRadio ref={ref} checked={checked} disabled={disabled} {...inputProps} />
            <StyledIndicator $checked={checked} />
            <StyledText>
                <StyledTitle>{title}</StyledTitle>
                {description && <StyledDescription>{description}</StyledDescription>}
            </StyledText>
            {icon && <StyledIcon className={icon} $color={iconColor} aria-hidden="true" />}
        </StyledCard>
    );
});

export default RadioCard;
